
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, ArrowLeft, Home, Loader2, XCircle } from 'lucide-react';
import { useAuthPayment } from '@/contexts/AuthPaymentContext';
import { toast } from '@/components/ui/sonner';

/**
 * Страница проверки статуса авторизации платежа
 * Только отображает информацию, не выполняет никаких действий с подписками
 */
const PaymentAuthStatus: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const { refreshAuthPayment } = useAuthPayment();

  // Параметры из URL
  const courierId = searchParams.get('courier_id');

  // Эффект для проверки параметров и переадресации пользователя
  useEffect(() => {
    const checkAndRedirect = async () => {
      try {
        // Обновляем контекст авторизационных платежей
        await refreshAuthPayment();
        
        // Показываем сообщение об успешной авторизации
        toast.success('Авторизация успешно завершена', {
          description: 'Пожалуйста, подождите...'
        });
        
        // Устанавливаем статус успеха
        setStatus('success');
        
        // Автоматический переход через 3 секунды
        setTimeout(() => {
          navigate('/courier');
        }, 3000);
      } catch (error) {
        console.error('Ошибка при обработке статуса авторизации:', error);
        setStatus('error');
        toast.error('Произошла ошибка при обработке статуса авторизации');
      }
    };

    checkAndRedirect();
  }, [courierId, navigate, refreshAuthPayment]);

  // Обработчик для возврата в панель курьера
  const handleBackToCourier = () => {
    navigate('/courier');
  };

  // Отображаем соответствующий контент в зависимости от статуса
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
      <Card className="w-full max-w-md">
        {status === 'loading' ? (
          <>
            <CardHeader className="text-center">
              <CardTitle>Проверка статуса авторизации</CardTitle>
              <CardDescription>Пожалуйста, подождите...</CardDescription>
            </CardHeader>
            <CardContent className="flex justify-center py-8">
              <Loader2 className="h-16 w-16 animate-spin text-primary" />
            </CardContent>
          </>
        ) : status === 'success' ? (
          <>
            <CardHeader className="text-center">
              <CardTitle className="text-green-600 flex items-center justify-center gap-2">
                <CheckCircle className="h-6 w-6" />
                Успешная авторизация
              </CardTitle>
              <CardDescription>Ваша карта успешно авторизована</CardDescription>
            </CardHeader>
            <CardContent className="text-center py-6">
              <p>Авторизация карты прошла успешно. Теперь вы можете пользоваться сервисом.</p>
              <p className="mt-4 text-muted-foreground">
                Вы будете автоматически перенаправлены на главную страницу.
              </p>
            </CardContent>
            <CardFooter className="flex flex-col gap-2">
              <Button 
                onClick={handleBackToCourier} 
                className="w-full flex items-center justify-center gap-2"
              >
                <Home className="h-4 w-4" />
                На главную
              </Button>
            </CardFooter>
          </>
        ) : (
          <>
            <CardHeader className="text-center">
              <CardTitle className="text-red-600 flex items-center justify-center gap-2">
                <XCircle className="h-6 w-6" />
                Ошибка авторизации
              </CardTitle>
              <CardDescription>Не удалось обработать авторизацию</CardDescription>
            </CardHeader>
            <CardContent className="text-center py-6">
              <p className="text-red-500">
                Произошла ошибка при обработке статуса авторизации
              </p>
              <p className="mt-4">
                Пожалуйста, попробуйте еще раз или обратитесь в службу поддержки.
              </p>
            </CardContent>
            <CardFooter className="flex flex-col gap-2">
              <Button 
                onClick={handleBackToCourier} 
                variant="default"
                className="w-full flex items-center justify-center gap-2"
              >
                <Home className="h-4 w-4" />
                На главную
              </Button>
            </CardFooter>
          </>
        )}
      </Card>
    </div>
  );
};

export default PaymentAuthStatus;
